<template>
    <div>
        <div
            class="mt-4 rounded-lg p-4 border"
            :class="[
                enabled
                    ? 'border-z-primary-500 bg-z-primary-25'
                    : 'border-iron-200',
            ]"
        >
            <div class="gap-2.5 flex">
                <Switch v-model:checked="enabled" class="mt-1" />
                <div>
                    <p class="text-sm font-medium text-iron-700">
                        {{ $t("event_types.bookable_window.title") }}
                    </p>
                    <p class="text-sm text-iron-500">
                        {{ $t("event_types.bookable_window.description") }}
                    </p>
                </div>
            </div>

            <div v-if="enabled" v-auto-animate class="mt-4 pl-[54px]">
                <div class="gap-4 flex flex-col">
                    <div class="max-w-sm gap-1 min-w-0 grid w-full">
                        <Label for="bookableFrom">{{
                            $t("event_types.bookable_window.from")
                        }}</Label>
                        <DatePicker
                            id="bookableFrom"
                            v-model="bookableFrom"
                            :locale="locale === 'cs' ? 'cs' : 'en'"
                            :placeholder="
                                $t('event_types.bookable_window.no_limit')
                            "
                        />
                    </div>

                    <div class="max-w-sm gap-1 min-w-0 grid w-full">
                        <Label for="bookableUntil">{{
                            $t("event_types.bookable_window.until")
                        }}</Label>
                        <DatePicker
                            id="bookableUntil"
                            v-model="bookableUntil"
                            :min-date="minUntilDate"
                            :locale="locale === 'cs' ? 'cs' : 'en'"
                            :placeholder="
                                $t('event_types.bookable_window.no_limit')
                            "
                        />
                        <p v-if="hasInvalidRange" class="text-sm text-red-600">
                            {{
                                $t("event_types.bookable_window.invalid_range")
                            }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { EventType } from "../types/EventType";
import { computed, ref, watch } from "vue";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/ui/date-picker/index.vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { vAutoAnimate } from "@formkit/auto-animate";
import { useI18n } from "vue-i18n";
import { useEventTypeStore } from "../stores/eventTypeStore";

interface Props {
    eventType: EventType;
}

const props = defineProps<Props>();

const { locale } = useI18n();
const eventTypeStore = useEventTypeStore();

const enabled = ref(
    props.eventType.bookable_from !== null ||
        props.eventType.bookable_until !== null,
);
const bookableFrom = ref(props.eventType.bookable_from ?? "");
const bookableUntil = ref(props.eventType.bookable_until ?? "");

watch(enabled, (value) => {
    if (!value || bookableFrom.value !== "" || bookableUntil.value !== "") {
        save();
    }
});

const minUntilDate = computed(() =>
    bookableFrom.value !== "" ? new Date(bookableFrom.value) : undefined,
);

const hasInvalidRange = computed(
    () =>
        bookableFrom.value !== "" &&
        bookableUntil.value !== "" &&
        new Date(bookableUntil.value) < new Date(bookableFrom.value),
);

watch(bookableFrom, (value) => {
    if (
        value !== "" &&
        bookableUntil.value !== "" &&
        new Date(bookableUntil.value) < new Date(value)
    ) {
        const endOfFromDay = new Date(value);
        endOfFromDay.setHours(23, 59, 59, 0);
        bookableUntil.value = endOfFromDay.toISOString();

        return;
    }

    if (enabled.value) {
        save();
    }
});

watch(bookableUntil, (value) => {
    const endOfPickedDay = toEndOfDay(value);

    if (endOfPickedDay !== null) {
        bookableUntil.value = endOfPickedDay;

        return;
    }

    if (enabled.value) {
        save();
    }
});

function toEndOfDay(value: string): string | null {
    if (value === "") {
        return null;
    }

    const date = new Date(value);

    if (
        date.getHours() !== 0 ||
        date.getMinutes() !== 0 ||
        date.getSeconds() !== 0
    ) {
        return null;
    }

    date.setHours(23, 59, 59, 0);

    return date.toISOString();
}

function save() {
    if (hasInvalidRange.value) {
        return;
    }

    router.put(
        route("app.event-types.bookable-window.update", {
            eventType: props.eventType.id,
        }),
        {
            bookable_from: enabled.value ? bookableFrom.value || null : null,
            bookable_until: enabled.value ? bookableUntil.value || null : null,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                eventTypeStore.refreshPreview();
            },
        },
    );
}
</script>
